<template>
  <div :class="containerClass">

    <h2>Add a Degree</h2>

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Degree*" label-for="program" label-class="form-label">
        <v-select
          id="program"
          class="form-control v-select-custom"
          :class="!$v.program.required && $v.program.$dirty ? 'form-control is-invalid' : ''"
          label="title"
          v-model="program"
          :reduce="p => p.id"
          :loading="areProgramsLoading"
          :options="degreeOptions"
          placeholder="Select a degree"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!program" v-bind="attributes" v-on="events" />
          </template>
          <template slot="option" slot-scope="option">
            <span>{{ option.title }}</span>
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.program.required && $v.program.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group v-if="program === 'other'" label="Other" label-for="degreeOther" label-class="form-label">
        <b-form-input
          id="degreeOther"
          placeholder="Enter degree name"
          v-model="degreeOther"
          :state="!$v.degreeOther.required && $v.degreeOther.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Institution*" label-for="institution" label-class="form-label">
        <b-form-input
          id="institution"
          placeholder="Enter institution"
          v-model="institution"
          :state="!$v.institution.required && $v.institution.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Year Awarded*" label-for="yearAwarded" class="form-label">
        <date-picker
          id="yearAwarded"
          v-model="yearAwarded"
          type="date"
          format="MM/DD/YYYY"
          value-type="YYYY-MM-DD"
          style="width: 100%"
          lang="en"
          placeholder="MM/DD/YYYY"
        />
        <b-form-invalid-feedback :state="!$v.yearAwarded.required && $v.yearAwarded.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group>
        <b-form-checkbox id="checkbox-req-org" v-model="isOrgReq" name="checkbox-req-org" switch>
          Mark this Document as Required for Organizations?
        </b-form-checkbox>
      </b-form-group>

      <b-form-group
        label="Upload Degree Document"
        label-for="document"
        label-class="form-label"
        class="row-align-items-center"
      >
        <b-progress :max="100" animated v-if="isUploading">
          <b-progress-bar :value="uploadPercent">
            <span>Uploading (<strong>{{ uploadPercent }} %</strong>)</span>
          </b-progress-bar>
        </b-progress>
        <span v-else-if="docUrl">
          <div class="row">
            <div class="col-md-10">
              <a
                href="#"
                @click.prevent="downloadFile({ fileUrl: docUrl, removeTimestamp: true })"
                v-b-popover.hover.top="'Download'"
                ><i class="fas fa-file text-primary"></i> {{ parseFileNameFromUrl(docUrl, true) }}
              </a>
            </div>
            <div class="text-right col-md-2">
              <a href="#" class="text-danger" @click.prevent="removeFile()"
                ><i class="fas fa-times-circle"></i> Remove
              </a>
            </div>
          </div>
        </span>
        <b-media class="align-items-center" vertical-align="center" v-else>
          <b-form-file
            placeholder="Select Document (max size: 10MB, only .pdf and image files allowed)"
            :browse-text="$t('generalMsgs.browse')"
            v-model="selectedUploadFile"
            @input="uploadFile()"
            :disabled="isUploading"
            accept=".pdf, image/*"
          />
        </b-media>
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : 'Add' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import axios from 'axios';
import { get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import { parseFileNameFromUrl } from '../../../common/utils';
import { required, requiredIf } from 'vuelidate/lib/validators';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/en';

export default {
  extends: Page,
  components: { DatePicker },

  data() {
    return {
      title: 'Add Degree',
      isLoading: false,
      program: null,
      degreeOther: '',
      institution: '',
      yearAwarded: null,
      docUrl: '',
      isOrgReq: false,
      isUploading: false,
      selectedUploadFile: null,
      uploadPercent: 0,
      allPrograms: [],
      areProgramsLoading: false,
    };
  },

  validations() {
    return {
      program: { required },
      institution: { required },
      yearAwarded: { required },
      degreeOther: { required: requiredIf(() => this.program === 'other') },
    };
  },

  computed: {
    degreeOptions() {
      return [...(this.allPrograms || []), { title: 'Other', id: 'other' }];
    },
    checklistContext() {
      const uniId = Number(this.$route.query.uniId);
      const programId = Number(this.$route.query.program_id);
      return {
        uniId: Number.isFinite(uniId) && uniId > 0 ? uniId : null,
        programId: Number.isFinite(programId) && programId > 0 ? programId : null,
      };
    },
  },

  methods: {
    ...mapActions('program', ['getAllPublicPrograms']),
    ...mapActions('fileDownload', ['downloadFile']),
    ...mapActions('student', [
      'createDegree',
      'getDegree',
      'updateDegree',
      'getStudentUploadPresignedUrl',
      'updateShowEarnedPointToast',
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    parseFileNameFromUrl,
    get,
    goBack() {
      this.$router.go(-1);
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      const response = await this.getAllPublicPrograms({ prg_type__in: 'degree,advanced_certificate' });
      this.allPrograms = response.data;
      this.areProgramsLoading = false;
    },
    async uploadFile(uploadType = 'documents') {
      this.uploadCancelTokenSource = axios.CancelToken.source();
      this.isUploading = true;

      try {
        const urlResp = await this.getStudentUploadPresignedUrl({
          file_name: this.selectedUploadFile.name,
          content_type: this.selectedUploadFile.type,
          upload_type: uploadType,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.selectedUploadFile,
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource.token,
          },
        });
        this.docUrl = urlResp.upload_url.split('?')[0];
      } catch (error) {
        this.selectedUploadFile = null;
      }

      this.uploadCancelTokenSource = null;
      this.uploadPercent = 0;
      this.isUploading = false;
    },
    removeFile() {
      this.selectedUploadFile = null;
      this.docUrl = null;
    },
    async fetchExisting() {
      try {
        const res = await this.getDegree(this.$route.params.id);
        this.program = res.data.program;
        this.degreeOther = res.data.degree_other;
        this.institution = res.data.institution;
        this.yearAwarded = res.data.year_awarded;
        this.docUrl = res.data.degree_url;
        this.isOrgReq = res.data.req_by_org;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async onSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            program: this.program === 'other' ? null : this.program,
            degree_other: this.degreeOther,
            institution: this.institution,
            year_awarded: this.yearAwarded,
            degree_url: this.docUrl,
            req_by_org: this.isOrgReq,
          };

          if (this.$route.params.id) {
            await this.updateDegree({ id: this.$route.params.id, data });
            this.makeToast({ variant: 'success', msg: 'Degree updated!' });
          } else {
            const createPayload = {
              ...data,
              ...(this.checklistContext.uniId && {
                entity_type: 'university',
                entity_id: this.checklistContext.uniId,
              }),
              ...(this.checklistContext.uniId && this.checklistContext.programId && {
                program_id: this.checklistContext.programId,
              }),
            };
            const resp = await this.createDegree(createPayload);
            this.makeToast({ variant: 'success', msg: 'Degree added!' });
            if (resp.data.data.points_earned > 0) {
              this.updateShowEarnedPointToast(resp.data.data.points_earned);
            } else {
              this.makeToast({
                variant: 'info',
                msg: 'Saved successfully. No checklist points awarded for this program requirement.',
              });
            }
          }
          setTimeout(() => {
            this.$router.push({ name: 'my-degrees' });
          }, 1000);
        } catch (err) {
          this.makeToast({
            variant: 'danger',
            msg: get(err, 'response.data.message') || get(err, 'response.data.error') || this.$t('generalMsgs.genErrorMsg'),
          });
        }
        this.isLoading = false;
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields.' });
      }
    },
  },

  async mounted() {
    this.fetchPrograms();
    if (this.$route.params.id) {
      this.fetchExisting();
    }
  },
};
</script>
